import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { echo } from '@laravel/echo-react';
import {
  markAssetInListCaches,
  mergeAssetPayloadIntoListCaches,
  mergeAssetPayloadIntoSingleCache,
  mergeVariantsIntoListCaches,
  mergeVariantsIntoSingleCache,
  removalNeedsRefresh,
  setAssetTrashedInSingleCache,
} from '@/hooks/websocket/mergeAssetPayload';
import type { AssetIdPayload, AssetUpdatedPayload, AssetVersionActivatedPayload } from '@/hooks/websocket/types';
import { useStaleViewStore } from '@/hooks/websocket/useStaleViewStore';
import { isDefined } from '@/utilities/guards';
import { QueryKey } from '@/lib/queryKeys';

/**
 * Module scope so the handlers that call `markStale` keep a stable identity across renders -
 * they feed the subscribe effect below, and a fresh array each render would rebuild the
 * channel. Trash is included because a row leaving this folder arrives in the bin, and a
 * viewer who refreshes here must not later meet the pre-event bin.
 */
const REFRESHED_QUERY_KEYS = [[QueryKey.AssetData], [QueryKey.Trash]];

/**
 * A batch duplicate broadcasts one `.asset.duplicated` per asset, often in a tight cluster -
 * without throttling this, each one refetched the active list query on its own.
 */
export const DUPLICATE_REFRESH_THROTTLE_MS = 4000;

export interface UseNodeAssetsChannelOptions {
  enabled?: boolean;
  /**
   * Whose screen this is, so the hook can drop events this viewer caused. Passed in
   * rather than read from `useAccount` here for the same reason the channel name is:
   * the page owns the identity, the hook owns the channel.
   */
  currentUserId?: string;
  /** Gone for good. Nothing can act on it, so it leaves the selection entirely. */
  onAssetDeleted?: (assetId: string) => void;
  /** Still exists, just moved in or out of the bin - the selection has to say which. */
  onAssetTrashStateChanged?: (assetId: string, trashed: boolean) => void;
}

/**
 * Keeps the asset caches in step with what other clients do to the node being
 * viewed. `channelName` is the full private channel (`folder.x`, `album.x`);
 * pass `undefined` when there is nothing to subscribe to.
 */
export const useNodeAssetsChannel = (
  channelName: string | undefined,
  options: UseNodeAssetsChannelOptions = {},
): void => {
  const { enabled = true, currentUserId, onAssetDeleted, onAssetTrashStateChanged } = options;
  const queryClient = useQueryClient();
  const markStale = useStaleViewStore((state) => state.markStale);

  // Excluding the actor's socket is the backend's first choice and it covers the request
  // path, but it cannot work where there is no request: "select all in this folder" then
  // Delete posts to /batch, which runs one queued BatchEditingJob per asset, and a worker
  // has no socket to exclude. Those broadcasts name the actor instead - `userId` is on the
  // payload exactly when the socket could not be used - so drop them here, the way the
  // Trash page already does. Without this the person who emptied the folder is told by
  // their own action that someone changed their view.
  //
  // The price, same as on the Trash channel, is that a second tab belonging to this user
  // is silenced too, and stays stale until something else refreshes it.
  const isOwnAction = useCallback(
    (event: { userId?: string }) => isDefined(currentUserId) && event.userId === currentUserId,
    [currentUserId],
  );

  const handleAssetUpdated = useCallback(
    (event: AssetUpdatedPayload) => {
      mergeAssetPayloadIntoSingleCache(queryClient, event);
      mergeAssetPayloadIntoListCaches(queryClient, event);
    },
    [queryClient],
  );

  const handleVersionActivated = useCallback(
    (event: AssetVersionActivatedPayload) => {
      mergeVariantsIntoSingleCache(queryClient, event);
      mergeVariantsIntoListCaches(queryClient, event);
    },
    [queryClient],
  );

  const pendingDuplicatedIds = useRef<Set<string>>(new Set());
  const pendingOwnDuplicatedIds = useRef<Set<string>>(new Set());
  const duplicateThrottleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastDuplicateFlushAt = useRef(0);

  /** A batch duplicate never learns its new ids up front, so this channel is the actor's only
   * completion signal too - their own ids skip the prompt but still need the list refetched. */
  const flushDuplicated = useCallback(() => {
    const otherIds = Array.from(pendingDuplicatedIds.current);
    const ownIds = Array.from(pendingOwnDuplicatedIds.current);
    pendingDuplicatedIds.current.clear();
    pendingOwnDuplicatedIds.current.clear();
    lastDuplicateFlushAt.current = Date.now();

    [...otherIds, ...ownIds].forEach((assetId) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset, assetId] });
    });

    if (ownIds.length > 0) {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData], refetchType: 'all' });
    }

    if (otherIds.length > 0) {
      markStale(REFRESHED_QUERY_KEYS);
    }
  }, [queryClient, markStale]);

  // Flushing rather than just clearing the timer: navigating away mid-batch must not lose
  // whatever ids arrived in the final, still-pending window.
  useEffect(
    () => () => {
      clearTimeout(duplicateThrottleTimer.current);
      if (pendingDuplicatedIds.current.size > 0 || pendingOwnDuplicatedIds.current.size > 0) {
        flushDuplicated();
      }
    },
    [flushDuplicated],
  );

  // Leading + trailing throttle, not a plain debounce - a long batch keeps arriving, and a
  // plain debounce would never settle.
  const handleAssetDuplicated = useCallback(
    (event: AssetIdPayload) => {
      if (isOwnAction(event)) {
        pendingOwnDuplicatedIds.current.add(event.id);
      } else {
        pendingDuplicatedIds.current.add(event.id);
      }

      const sinceLastFlush = Date.now() - lastDuplicateFlushAt.current;
      if (sinceLastFlush >= DUPLICATE_REFRESH_THROTTLE_MS) {
        flushDuplicated();
        return;
      }

      clearTimeout(duplicateThrottleTimer.current);
      duplicateThrottleTimer.current = setTimeout(flushDuplicated, DUPLICATE_REFRESH_THROTTLE_MS - sinceLastFlush);
    },
    [isOwnAction, flushDuplicated],
  );

  // The list is left exactly as it is in every case below: the row stays put until the
  // viewer takes the refresh, so nothing appears or vanishes from under them. What does
  // change is the state the inspector and toolbar read, because leaving those stale means
  // offering edits and actions the server will reject.

  const handleAssetTrashed = useCallback(
    (event: AssetIdPayload) => {
      if (isOwnAction(event)) {
        return;
      }

      // Asked before anything is written: this client's own mutation is what puts the list
      // query into fetchStatus 'fetching', the signal that it needs no prompt.
      const needsRefresh = removalNeedsRefresh(queryClient, event);

      // Greys the row where it stands - still selectable, because the asset still exists
      // and can be restored. The inspector reads the single cache for its banner.
      markAssetInListCaches(queryClient, event.id, { departed: 'trashed' });
      setAssetTrashedInSingleCache(queryClient, event.id, true);
      onAssetTrashStateChanged?.(event.id, true);

      if (needsRefresh) {
        markStale(REFRESHED_QUERY_KEYS);
      }
    },
    [isOwnAction, queryClient, onAssetTrashStateChanged, markStale],
  );

  const handleAssetDeleted = useCallback(
    (event: AssetIdPayload) => {
      if (isOwnAction(event)) {
        return;
      }

      // Asked first, for the same reason as above.
      const needsRefresh = removalNeedsRefresh(queryClient, event);

      // Permanent: there is nothing left to inspect or act on. The row keeps its place
      // rather than moving everything below it, but it is marked so the gallery greys it
      // out and refuses to select it, and it leaves the selection it may already be in.
      // Clears `departed` with it: an asset that was trashed and is now gone is simply
      // gone, and carrying both states would leave the renderer choosing between them.
      markAssetInListCaches(queryClient, event.id, { deleted: true, departed: undefined });
      onAssetDeleted?.(event.id);

      if (needsRefresh) {
        markStale(REFRESHED_QUERY_KEYS);
      }
    },
    [isOwnAction, queryClient, onAssetDeleted, markStale],
  );

  const handleAssetRestored = useCallback(
    (event: AssetIdPayload) => {
      if (isOwnAction(event)) {
        return;
      }

      // No `removalNeedsRefresh` here, unlike the two above: it asks whether a list on
      // screen still shows the asset, and a restored one is not on this list - it was
      // filtered out while it sat in the bin and is arriving now. Asking would answer
      // false every time and the row would never appear, so this always prompts.
      markAssetInListCaches(queryClient, event.id, { departed: undefined });
      setAssetTrashedInSingleCache(queryClient, event.id, false);
      onAssetTrashStateChanged?.(event.id, false);
      markStale(REFRESHED_QUERY_KEYS);
    },
    [isOwnAction, queryClient, onAssetTrashStateChanged, markStale],
  );

  useEffect(() => {
    if (!channelName || !enabled) {
      return;
    }

    const channel = echo().private(channelName);
    channel.listen('.asset.updated', handleAssetUpdated);
    channel.listen('.asset.version.activated', handleVersionActivated);
    channel.listen('.asset.trashed', handleAssetTrashed);
    channel.listen('.asset.deleted', handleAssetDeleted);
    channel.listen('.asset.restored', handleAssetRestored);
    channel.listen('.asset.duplicated', handleAssetDuplicated);

    return () => {
      channel.stopListening('.asset.updated', handleAssetUpdated);
      channel.stopListening('.asset.version.activated', handleVersionActivated);
      channel.stopListening('.asset.trashed', handleAssetTrashed);
      channel.stopListening('.asset.deleted', handleAssetDeleted);
      channel.stopListening('.asset.restored', handleAssetRestored);
      channel.stopListening('.asset.duplicated', handleAssetDuplicated);
      echo().leaveChannel(`private-${channelName}`);
    };
  }, [
    channelName,
    enabled,
    handleAssetUpdated,
    handleVersionActivated,
    handleAssetTrashed,
    handleAssetDeleted,
    handleAssetRestored,
    handleAssetDuplicated,
  ]);
};
